import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { useStore } from '../store/useStore';
import {
  getFormSubmission, updateFormSubmission,
  getFormTemplates, getFormTemplatePdfUrl, getFilledPdfUrl,
} from '../utils/api';
import SignaturePad from './SignaturePad';
import styles from './PdfFormFiller.module.css';

// Walk the schema's required fields and return labels of any that are
// empty in the values map. Mirrors the server's collectRequiredButEmpty.
function collectMissingRequired(fields, values) {
  const missing = [];
  const radioRequired = new Set();
  const radioFilled   = new Set();
  for (const f of fields) {
    if (!f.required) continue;
    if (f.type === 'radio') {
      radioRequired.add(f.name);
      if (values[f.name]) radioFilled.add(f.name);
      continue;
    }
    const v = values[f.name];
    let empty;
    if (f.type === 'checkbox') empty = !(v === true || v === 'true' || v === 'yes' || v === 'on');
    else                       empty = v == null || v === '';
    if (empty) missing.push(f.label || f.name);
  }
  for (const name of radioRequired) {
    if (!radioFilled.has(name)) {
      const f = fields.find((x) => x.name === name);
      missing.push(f?.label || name);
    }
  }
  return missing;
}

// In-browser AcroForm filler. Reuses pdfjs-dist (already loaded by the
// drawing viewer) to render the template PDF page-by-page into canvases,
// then overlays absolutely-positioned HTML inputs aligned with each
// AcroForm field's rectangle.
//
// Coordinate space: PDF user-space origin is bottom-left; CSS origin is
// top-left. We flip Y on the way in so an input lines up with the field's
// printed location at any zoom.
//
// Saving:
//   `data.fields` is a flat { fieldName: value } map. Text → string,
//   checkbox → 'true' / 'false', radio → option_value, dropdown → selected
//   option string. Save Draft / Submit reuse the existing /api/form-
//   submissions/:id PATCH; the server's filled-PDF endpoint reads this map
//   to fill + flatten on demand.

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url,
).toString();

// Render scale — how big the canvas is rasterized at relative to the PDF's
// native point size. 1.5 keeps text crisp at typical screen sizes; higher
// would be sharper on retina but uses 4x the memory per .5 step.
const RENDER_SCALE = 1.5;

export default function PdfFormFiller({ submissionId, readOnly, onClose }) {
  const { role, project } = useStore();
  const isWriter = role === 'admin' || role === 'pm';

  const [sub, setSub]         = useState(null);
  const [template, setTemplate] = useState(null);
  const [data, setData]       = useState({ fields: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const dirtyRef = useRef(false);

  // ── Load submission + its template (we need template.schema for fields) ──
  async function refresh() {
    setLoading(true);
    try {
      const s = await getFormSubmission(submissionId);
      setSub(s);
      setData(s.data || { fields: {} });
      // We don't have a single-template endpoint; fetch the project's list
      // and pick. List is small (handful of templates) so this is fine.
      const list = await getFormTemplates(s.project_id);
      const t = list.find((x) => x.id === s.template_id);
      if (!t) throw new Error('template not found');
      setTemplate(t);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { if (submissionId) refresh(); /* eslint-disable-next-line */ }, [submissionId]);

  const submitted = sub?.status === 'submitted';
  const canEdit   = !readOnly && (!submitted || isWriter);

  function setField(name, value) {
    setData((d) => ({ ...d, fields: { ...(d.fields || {}), [name]: value } }));
    dirtyRef.current = true;
  }

  async function handleSaveDraft() {
    if (!canEdit) return;
    setSaving(true);
    try {
      const fresh = await updateFormSubmission(sub.id, { data });
      setSub(fresh);
      dirtyRef.current = false;
      toast.success('Draft saved');
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  }

  async function handleSubmit() {
    if (!canEdit) return;
    // Pre-check required fields client-side so we can give a friendly inline
    // error before round-tripping. Server also validates.
    const missing = collectMissingRequired(template?.schema?.fields || [], data.fields || {});
    if (missing.length) {
      toast.error(`Required: ${missing.join(', ')}`);
      return;
    }
    if (!window.confirm('Submit this form? Once submitted, only PM/Admin can edit.')) return;
    setSubmitting(true);
    try {
      const fresh = await updateFormSubmission(sub.id, { data, status: 'submitted' });
      setSub(fresh);
      dirtyRef.current = false;
      toast.success('Submitted');
      onClose?.();
    } catch (err) {
      // The api.js interceptor already pulls the server's friendly error
      // into err.message (e.g. "Cannot submit — required fields are empty: …").
      toast.error(err.message);
    }
    finally { setSubmitting(false); }
  }

  async function handleUnsubmit() {
    if (!isWriter || !submitted) return;
    if (!window.confirm('Unlock this submitted form so it can be edited again?')) return;
    setSubmitting(true);
    try {
      const fresh = await updateFormSubmission(sub.id, { status: 'draft' });
      setSub(fresh);
      toast.success('Unlocked');
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  }

  useEffect(() => {
    function onBefore(e) {
      if (dirtyRef.current && canEdit) {
        e.preventDefault();
        e.returnValue = '';
      }
    }
    window.addEventListener('beforeunload', onBefore);
    return () => window.removeEventListener('beforeunload', onBefore);
  }, [canEdit]);

  if (loading || !sub || !template) {
    return (
      <div className={styles.page}>
        <div className={styles.empty}>Loading…</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button className={styles.backBtn} onClick={onClose}>
          <BackIcon /> Back to forms
        </button>
        <div className={styles.headerCenter}>
          <span className={styles.headerTitle}>{template.name}</span>
          <span className={`${styles.statusPill} ${submitted ? styles.statusSubmitted : styles.statusDraft}`}>
            {sub.status.toUpperCase()}
          </span>
        </div>
        <div className={styles.headerActions}>
          {/* Always offer the filled PDF download — useful both during fill
              (preview the current state) and after submit (final artifact). */}
          <a
            className={styles.btnSecondary}
            href={getFilledPdfUrl(sub.id, { download: true })}
            target="_blank"
            rel="noopener noreferrer"
          >
            <DownloadIcon /> Filled PDF
          </a>
          {canEdit && (
            <>
              <button className={styles.btnSecondary} onClick={handleSaveDraft} disabled={saving || submitting}>
                {saving ? 'Saving…' : 'Save Draft'}
              </button>
              <button className={styles.btnPrimary} onClick={handleSubmit} disabled={saving || submitting}>
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </>
          )}
          {submitted && isWriter && readOnly && (
            <button className={styles.btnSecondary} onClick={handleUnsubmit} disabled={submitting}>
              {submitting ? 'Unlocking…' : 'Unlock'}
            </button>
          )}
        </div>
      </header>

      <div className={styles.metaStrip}>
        <MetaItem label="Submitted by">{sub.submitted_by || '—'}</MetaItem>
        <MetaItem label="Created by">{sub.created_by || '—'}</MetaItem>
        <MetaItem label="Last update">
          {sub.updated_at ? format(new Date(sub.updated_at), 'MMM d, yyyy h:mm a') : '—'}
        </MetaItem>
        <MetaItem label="Fields">
          {template.schema?.fields?.length || 0} · {template.schema?.page_count || 0} pages
        </MetaItem>
      </div>

      <div className={styles.scroll}>
        <PdfPagesWithOverlays
          templateId={template.id}
          schema={template.schema}
          values={data.fields || {}}
          onChange={setField}
          readOnly={!canEdit}
        />
      </div>
    </div>
  );
}

function MetaItem({ label, children }) {
  return (
    <div className={styles.metaItem}>
      <span className={styles.metaLabel}>{label}</span>
      <span className={styles.metaValue}>{children}</span>
    </div>
  );
}

// ── PdfPagesWithOverlays ────────────────────────────────────────────────────
// Loads the blank template PDF, renders each page once into a canvas, and
// renders an absolute-positioned overlay div per page with positioned
// inputs for that page's AcroForm fields.
function PdfPagesWithOverlays({ templateId, schema, values, onChange, readOnly }) {
  const [pageCount, setPageCount] = useState(0);
  const containerRef = useRef(null);
  const pagesByIdx   = useRef({}); // page index → { canvas, viewport }
  const [loadError, setLoadError] = useState(null);
  const [loaded, setLoaded] = useState(false);

  // Group fields by page for fast lookup during render.
  const fieldsByPage = useMemo(() => {
    const map = {};
    for (const f of schema.fields || []) {
      if (!map[f.page]) map[f.page] = [];
      map[f.page].push(f);
    }
    return map;
  }, [schema]);

  // Group radio widgets by their shared field name. The user clicks on
  // any one to "select" the group; we map back to its option_value.
  const radioGroupsByPage = useMemo(() => {
    const map = {};
    for (const f of schema.fields || []) {
      if (f.type !== 'radio') continue;
      const key = `${f.page}::${f.name}`;
      if (!map[key]) map[key] = [];
      map[key].push(f);
    }
    return map;
  }, [schema]);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false); setLoadError(null);

    (async () => {
      try {
        const url = getFormTemplatePdfUrl(templateId);
        const doc = await pdfjsLib.getDocument(url).promise;
        if (cancelled) return;
        setPageCount(doc.numPages);

        for (let i = 1; i <= doc.numPages; i++) {
          if (cancelled) return;
          const page = await doc.getPage(i);
          const viewport = page.getViewport({ scale: RENDER_SCALE });
          const canvas = pagesByIdx.current[i - 1]?.canvas;
          if (!canvas) continue;
          canvas.width  = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width  = `${viewport.width}px`;
          canvas.style.height = `${viewport.height}px`;
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          pagesByIdx.current[i - 1].viewport = viewport;
          // Force a re-render so overlays know viewport was assigned
          if (!cancelled) setLoaded((v) => !v);
        }
        if (!cancelled) setLoaded(true);
      } catch (err) {
        if (!cancelled) setLoadError(err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [templateId]);

  function handleRadioChange(name, optionValue) {
    onChange(name, optionValue);
  }

  // PDF rect → CSS rect. PDF y is from bottom; flip with page height.
  function rectToCSS(rect, pageSize) {
    const [x, y, w, h] = rect;
    const left = x * RENDER_SCALE;
    const top  = (pageSize.h - y - h) * RENDER_SCALE;
    return {
      left,
      top,
      width:  w * RENDER_SCALE,
      height: h * RENDER_SCALE,
    };
  }

  if (loadError) {
    return <div className={styles.errorBox}>Could not load template PDF: {loadError}</div>;
  }

  return (
    <div ref={containerRef} className={styles.pagesWrap}>
      {schema.page_sizes?.map((pageSize, i) => {
        const fieldsOnPage = fieldsByPage[i] || [];
        // Skip rendering radios as individual inputs in the loop below;
        // we render them grouped per (name) so they share name= attribute
        // and get native single-selection behavior.
        const radiosOnPage = new Set();
        for (const f of fieldsOnPage) {
          if (f.type === 'radio') radiosOnPage.add(f.name);
        }
        const radioGroupNames = Array.from(radiosOnPage);

        return (
          <div key={i} className={styles.pageWrap}>
            <div className={styles.pageBadge}>Page {i + 1} of {schema.page_count}</div>
            <div
              className={styles.pageBox}
              style={{
                width:  pageSize.w * RENDER_SCALE,
                height: pageSize.h * RENDER_SCALE,
              }}
            >
              <canvas
                ref={(el) => { if (el) pagesByIdx.current[i] = { ...(pagesByIdx.current[i] || {}), canvas: el }; }}
                className={styles.pageCanvas}
              />
              <div className={styles.overlay}>
                {/* Non-radio fields */}
                {fieldsOnPage.filter((f) => f.type !== 'radio').map((f) => {
                  const css = rectToCSS(f.rect, pageSize);
                  const value = values[f.name];
                  // Highlight required fields with a red-ish outline so users
                  // know what they must fill before submit.
                  const cls = `${styles.fieldBox} ${f.required ? styles.required : ''}`;
                  return (
                    <div
                      key={f.id}
                      className={cls}
                      style={css}
                      title={`${f.label || f.name}${f.required ? ' (required)' : ''}`}
                    >
                      {f.type === 'text' && (
                        <input
                          type="text"
                          className={styles.input}
                          value={value || ''}
                          disabled={readOnly}
                          onChange={(e) => onChange(f.name, e.target.value)}
                        />
                      )}
                      {f.type === 'number' && (
                        <input
                          type="number"
                          inputMode="decimal"
                          className={styles.input}
                          value={value || ''}
                          disabled={readOnly}
                          onChange={(e) => onChange(f.name, e.target.value)}
                        />
                      )}
                      {f.type === 'date' && (
                        <input
                          type="date"
                          className={styles.input}
                          value={value || ''}
                          disabled={readOnly}
                          onChange={(e) => onChange(f.name, e.target.value)}
                        />
                      )}
                      {f.type === 'signature' && (
                        // Signature pad is bigger than a typical input — its
                        // surrounding fieldBox is sized to the field's rect
                        // so the canvas fills it.
                        <SignaturePad
                          value={value || null}
                          onChange={(dataUrl) => onChange(f.name, dataUrl || '')}
                          disabled={readOnly}
                          height={Math.max(40, css.height)}
                        />
                      )}
                      {f.type === 'checkbox' && (
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={value === true || value === 'true' || value === 'yes' || value === 'on'}
                          disabled={readOnly}
                          onChange={(e) => onChange(f.name, e.target.checked ? 'true' : 'false')}
                        />
                      )}
                      {f.type === 'dropdown' && (
                        <select
                          className={styles.input}
                          value={value || ''}
                          disabled={readOnly}
                          onChange={(e) => onChange(f.name, e.target.value)}
                        >
                          <option value=""></option>
                          {(f.options || []).map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  );
                })}

                {/* Radio groups: render each widget as its own radio input,
                    sharing name= so the browser handles single-selection. */}
                {radioGroupNames.map((name) => {
                  const widgets = radioGroupsByPage[`${i}::${name}`] || [];
                  const selected = values[name];
                  return widgets.map((w) => {
                    const css = rectToCSS(w.rect, pageSize);
                    return (
                      <div key={w.id} className={styles.fieldBox} style={css} title={`${name} = ${w.option_value}`}>
                        <input
                          type="radio"
                          className={styles.radio}
                          name={`radio_${name}_${i}`}
                          value={w.option_value}
                          checked={selected === w.option_value}
                          disabled={readOnly}
                          onChange={() => handleRadioChange(name, w.option_value)}
                        />
                      </div>
                    );
                  });
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Icons ──
const BackIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M7.5 2L3.5 6L7.5 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const DownloadIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
    <path d="M8 1v9M5 7l3 3 3-3M2 12v2a1 1 0 001 1h10a1 1 0 001-1v-2"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
